'use client'

import { createContext, useContext } from 'react'

import { resolveClientCopy } from './client-copy'
import type { SiteLabels } from './content/defaults/site-content'
import type { Testimonial } from './content/testimonials'
import type { PublicSiteSettings } from './public-settings'

/** CMS copy the client components need, read once in the (site) layout. */
export type ClientCopy = {
  siteLabels: SiteLabels
}

type SiteSettingsContextValue = PublicSiteSettings & {
  testimonial: Testimonial
  googleReviewsUrl: string
  copy?: ClientCopy
}

const SiteSettingsContext = createContext<SiteSettingsContextValue | null>(null)

export function SiteSettingsProvider({
  value,
  children,
}: {
  value: SiteSettingsContextValue
  children: React.ReactNode
}) {
  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>
}

/**
 * Read site context (settings + featured testimonial + reviews URL) inside a
 * client component. Must be inside a SiteSettingsProvider (mounted in
 * src/app/(site)/layout.tsx). For server components, import the dedicated
 * getters (getSiteSettings, getFeaturedTestimonial, getNavigation).
 */
export function useSiteSettings(): SiteSettingsContextValue {
  const ctx = useContext(SiteSettingsContext)
  if (!ctx) {
    throw new Error('useSiteSettings must be used inside SiteSettingsProvider.')
  }
  return ctx
}

/**
 * CMS copy for client components. Falls back to the code defaults when there
 * is no provider, which is also what an error boundary needs if the CMS is
 * the thing that failed.
 */
export function useClientCopy(): ClientCopy {
  return resolveClientCopy(useContext(SiteSettingsContext)?.copy)
}
